import { useI18n } from '@/hooks/useI18n';
import { Link } from 'react-router-dom';
import { Tile } from '@/components/primitives/Tile';
import { ErrorInline } from '@/components/primitives/ErrorInline';
import { Skeleton } from '@/components/ui/skeleton';
import { useLinkReadiness } from '@/hooks/queries/useLinkReadiness';
import { formatNumber } from '@/lib/format';
import { SectionHeader } from './SectionHeader';
import { AnalyticsEmpty } from './AnalyticsEmpty';

export function LinkReadinessSection() {
  const { t } = useI18n();
  const { data, isLoading, isError, error, refetch } = useLinkReadiness({
    periodDays: 14,
    limit: 20,
  });

  return (
    <section className="min-w-0 space-y-3">
      <SectionHeader
        title={t('Potentiel pour un lien en story', 'Story link potential')}
        subtitle={t(
          '14 derniers jours · Score = vues gagnées × stabilité. Un indicateur de trafic, pas une garantie de clics.',
          'Last 14 days · Score = views gained × stability. A traffic indicator, not a guarantee of clicks.',
        )}
      />
      <Tile padded>
        {isError ? (
          <ErrorInline error={error} onRetry={() => void refetch()} />
        ) : isLoading && !data ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className="h-9 w-full rounded-[8px]" />
            ))}
          </div>
        ) : !data || data.length === 0 ? (
          <AnalyticsEmpty
            title={t('Pas assez de données', 'Not enough data')}
            description={t(
              'Au moins 3 jours de mesures exploitables par compte sont nécessaires sur les 14 derniers jours.',
              'At least 3 days of usable measurements per account are needed within the last 14 days.',
            )}
          />
        ) : (
          <div className="max-w-full overflow-x-auto rounded-[10px] border border-line-1">
            <table className="w-full min-w-[560px] text-[13px]">
              <thead className="bg-bg-2 text-left">
                <tr>
                  <th scope="col" className="px-3 py-2 font-semibold text-text-2">
                    {t('Compte', 'Account')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-right font-semibold text-text-2">
                    {t('Vues gagnées', 'Views gained')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-right font-semibold text-text-2">
                    {t('Moy. / jour mesuré', 'Avg. / measured day')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-right font-semibold text-text-2">
                    {t('Stabilité', 'Stability')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-right font-semibold text-text-2">
                    Score
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.map((row) => (
                  <tr
                    key={row.accountId}
                    className="border-t border-line-1 transition-colors hover:bg-bg-2"
                  >
                    <td className="px-3 py-2">
                      <Link
                        to={`/accounts/${row.accountId}`}
                        className="inline-flex min-h-10 items-center rounded font-medium text-text-1 underline-offset-4 hover:text-violet hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet"
                      >
                        @{row.username}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-text-1">
                      {formatNumber(row.totalViews)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-text-3">
                      {formatNumber(Math.round(row.dailyMean))}
                    </td>
                    <td
                      className={`px-3 py-2 text-right font-medium tabular-nums ${stabilityColor(row.stabilityPct)}`}
                    >
                      {row.stabilityPct.toFixed(0)}%
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-text-1">
                      {formatNumber(Math.round(row.score))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Tile>
    </section>
  );
}

function stabilityColor(pct: number): string {
  if (pct >= 70) return 'text-emerald';
  if (pct >= 40) return 'text-amber';
  return 'text-rose';
}
